import { Injectable, signal } from '@angular/core';
import { denialMessageKey, isRefusal, validationMessagesOf } from '../http-problem';

/**
 * The app's one channel for reporting the outcome of a write.
 *
 * Two shapes, deliberately different in weight:
 * - **Success is a toast** — it auto-dismisses, and the user has already seen the dialog close and
 *   the list refresh, so a confirmation they must click away is one obstacle per save.
 * - **Failure is a modal** — a write that did not happen must not be missable. It blocks until it is
 *   acknowledged, because the alternative is what this replaced: a dialog that sat there doing
 *   nothing while a 403 came back with an empty body.
 *
 * Messages are **translation keys**, never prose, matching the convention every load-error already
 * follows; the component resolves them with `TranslatePipe`.
 */

/** How long a success toast stays up. Long enough to read a short sentence, short enough to ignore. */
export const TOAST_DURATION_MS = 4000;

export interface Toast {
  /** Monotonic, so `@for` can track by it and a repeated identical message still animates as new. */
  id: number;
  messageKey: string;
}

export interface ErrorNotice {
  titleKey: string;
  messageKey: string;
  /**
   * Why the server refused, when it says — a second line under the message, never a replacement for
   * it. "Which write failed" and "why it was refused" are two different facts, and folding them into
   * one key would turn six operation messages into eighteen operation×reason ones.
   */
  reasonKey: string | null;
  /**
   * The same second line, for a rejected payload rather than a refused one: the API names the rule
   * that was broken, so its own words are shown. At most one of the two is ever set, and a status
   * that carries neither leaves the modal to its operation message alone.
   */
  reasonText: string | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private nextId = 1;

  readonly toasts = signal<readonly Toast[]>([]);
  readonly error  = signal<ErrorNotice | null>(null);

  /** Report a completed create/update/delete. */
  success(messageKey: string): void {
    const id = this.nextId++;
    this.toasts.update(list => [...list, { id, messageKey }]);
    setTimeout(() => this.dismiss(id), TOAST_DURATION_MS);
  }

  /**
   * Report a write that did not happen. `titleKey` defaults to the generic heading; pass one only
   * when the specific operation reads better in the title than in the body.
   */
  failure(messageKey: string, titleKey = 'notify_error_title'): void {
    this.error.set({ titleKey, messageKey, reasonKey: null, reasonText: null });
  }

  /**
   * Report a failed write, reading the reason out of the server's response when it gives one.
   *
   * This is what a `subscribe`'s `error:` handler should call; plain `failure` is for a refusal the
   * client decided on its own.
   *
   * The two reason lines are read from the two statuses that carry one: a refusal (401/403) resolves
   * to a translated denial, a rejected payload (400) to the API's own account of the rule it broke.
   * A status carrying neither — a 500, a dropped connection — is left without a reason line, since
   * "you may not have permission" reads as a diagnosis and would be a guess.
   */
  failureFrom(error: unknown, messageKey: string, titleKey = 'notify_error_title'): void {
    const reasonText = validationMessagesOf(error);
    const reasonKey  = reasonText === null && isRefusal(error) ? denialMessageKey(error) : null;
    this.error.set({ titleKey, messageKey, reasonKey, reasonText });
  }

  dismiss(id: number): void {
    this.toasts.update(list => list.filter(toast => toast.id !== id));
  }

  dismissError(): void {
    this.error.set(null);
  }
}
